import { Directive, ElementRef, Input, NgZone } from '@angular/core';
import { NgInject } from '@df/ng/ng-inject';
import { TimeoutService } from '@df/ng/timeout.service';
import { fromEvent } from 'rxjs';
import { IZippyEvent, ZippyAbstract, ZippyEventType } from '@df/legacy/ui/zippy/zippy.abstract';

/**
 * Creates directive that applies classes to element depending on zippy state.
 *
 * @usage
 * ```html
 * <!-- click only -->
 * <div zippyOpen="group.name"></div>
 *
 * <!-- click only -->
 * <div zippyOpen="group.name" zippyOpenEvent="click"></div>
 *
 * <!-- click or hover -->
 * <div zippyOpen="group.name" zippyOpenEvent="click,hover"></div>
 *
 * <!-- hover only -->
 * <div zippyOpen="group.name" zippyOpenEvent="hover"></div>
 * ```
 */
@Directive({
    selector: '[zippyOpen]',
    standalone: false
})
export class ZippyOpenDirective extends ZippyAbstract {
    protected isOnClick = true;
    protected isOnHover = false;

    /** @internal */
    protected timeout?: number;

    protected clicks = 0;

    @Input()
    inDelay = 0;

    @Input()
    outDelay: number = 0;

    /**
     * If set then clicking will not trigger opening
     * @type boolean
     */
    @Input()
    zippyOpenOff = false;

    /**
     * If set then mouseout will not close opened zippy
     * @type boolean
     */
    @Input()
    zippyCloseOff = false;

    /**
     * Zippy name setter
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    @Input()
    set zippyOpen(name: string | (string | number)[]) {
        this.parseName(name);
    }

    /**
     * Parses events list to mark events listened
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    @Input()
    set zippyOpenEvent(eventsList: string | string[]) {
        if (eventsList) {
            const events: string[] = typeof eventsList === 'string' ? eventsList.split(',') : eventsList;
            this.isOnClick = events.includes('click') || this.client.isTouchy;
            this.isOnHover = events.includes('hover');
        } else {
            this.isOnClick = true;
            this.isOnHover = false;
        }
    }

    @NgInject(NgZone)
    protected ngZone!: NgZone;

    constructor(protected elementRef: ElementRef) {
        super();

        this.timeoutService.async(() => {
            this.subscriptions.add(
                fromEvent<Event>(this.elementRef.nativeElement, 'click').subscribe((event: Event) => {
                    if (!event.defaultPrevented || this.client.isCms) {
                        this.onClick(event);
                    }
                })
            );

            this.subscriptions.add(fromEvent(this.elementRef.nativeElement, 'mouseenter').subscribe(() => this.onMouseIn()));

            this.subscriptions.add(fromEvent(this.elementRef.nativeElement, 'mouseleave').subscribe(() => this.onMouseOut()));
        });
    }

    /**
     * Click event listener.
     *
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {[type]} 'click'
     */
    protected onClick(event?: Event) {
        if (!this.clicks) {
            this.timeoutService.setTimeout(
                () => {
                    if (this.clicks === 1) {
                        if (!this.zippyOpenOff) {
                            if (this.client.isCms) {
                                this.toggle();
                            } else if (this.isOnClick) {
                                // if not yet visible then prevent events
                                // we need this to work with touchy devices: first time you click only zippy should act,
                                // once open should do what is below that
                                if (event && !this.isOpen()) {
                                    event.preventDefault();
                                    // we need to pass custom value because `event.defaultPrivented` is reseted in `optHref` directive
                                    (event as Event).preventOptHref = true;
                                }

                                this.open();
                            }
                        }
                    }
                    this.clicks = 0;
                },
                this.client.isCms ? 300 : 0
            );
        }

        this.clicks++;
    }

    /**
     * Mouseover event listener.
     *
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {[type]} 'click'
     */
    protected onMouseIn() {
        if (!this.zippyOpenOff) {
            if ((!this.client.isCms || (this.id && (this.id.group || this.id.name).match(/^cms/))) && this.isOnHover) {
                this.clear();
                this.timeout = this.timeoutService.setTimeout(() => this.open(), this.inDelay);
            }
        }
    }

    /**
     * Mouseout event listener.
     *
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {[type]} 'click'
     */
    protected onMouseOut() {
        if ((!this.client.isCms || (this.id && (this.id.group || this.id.name).match(/^cms/))) && this.isOnHover && !this.zippyCloseOff) {
            this.clear();
            this.timeout = this.timeoutService.setTimeout(() => this.close(), this.outDelay);
        }
    }

    /**
     * Sends open request
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    protected open() {
        this.subject.next({
            id: this.id,
            type: ZippyEventType.open
        });
    }

    /**
     * Sends close request
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    protected close() {
        if (this.zippyCloseOff) {
            return;
        }

        this.subject.next({
            id: this.id,
            type: ZippyEventType.close
        });
    }

    /**
     * Toggles zippy
     */
    protected toggle() {
        if (this.isOpen()) {
            this.close();
        } else {
            this.open();
        }
    }

    /**
     * When another component opens or closes our target then cancel whatever we've been planning
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    protected onEvent(event: IZippyEvent) {
        if (!event.stopped) {
            switch (event.type) {
                case ZippyEventType.open:
                    if (this.checkEventMatchExact(event)) {
                        this.clear();
                    }
                    break;
                case ZippyEventType.close:
                    if (event.id.isGroup ? this.checkEventMatchGroup(event) : this.checkEventMatchExact(event)) {
                        this.clear();
                    }
                    break;
            }
        }
    }

    /**
     * Clears timeout.
     */
    protected clear() {
        clearTimeout(this.timeout);
    }
}
